/**
 * PDP UGC gallery toggle.
 *
 * Adds a thumbnail button over the product gallery that swaps it between the
 * product's own media and UGC. The UGC view is built from two sources, in this
 * order:
 *   1. the product's `custom.ugc` files (videos and images), published as JSON
 *      by snippets/ugc-metafield-media.liquid;
 *   2. the product's own Flowbox photos, as fetched by the PDP's image strip
 *      block (snippets/pdp-ugc-strip.liquid), or, when a template still uses
 *      the older standalone Flowbox section, read straight out of its widget
 *      once it has rendered.
 * Flowbox decides whether there is a button at all: with no Flowbox photos the
 * gallery gets no button, even when the product has `custom.ugc` files. Those
 * files only lead the reel once Flowbox has something.
 *
 * Why it rebuilds the gallery instead of moving nodes around:
 * this theme runs two different gallery engines off the same markup —
 * a CSS collage on desktop and a Slick carousel below 768px — and Slick is
 * bundled inside theme.js, so `jQuery.fn.slick` is not reachable from here.
 * theme.initProductGallery() / theme.destroyProductGallery() are exposed
 * globally though, so the swap is: destroy -> replace the .slide/.thumbnail
 * children -> init. The theme rebuilds whichever engine the viewport needs.
 * (initProductGallery early-returns while .gallery still carries
 * .gallery-initialised, so that class has to be cleared in between.)
 *
 * Flowbox notes:
 *   - posts render as .bzfy-c-carousel-post[data-post-key]; the carousel clones
 *     them, so dedupe on the key.
 *   - image URLs are https://cdn.flbx.io/image/<base64 JSON>, where the JSON
 *     carries edits.resize.width. Re-encoding it with a larger width is how we
 *     get gallery-sized art out of a widget that only asks for ~360px.
 */
(function () {
  'use strict';

  // The older standalone Flowbox section's widget; the PDP strip block, when
  // present, is used instead (STRIP_SELECTOR).
  var FLOWBOX_SELECTOR = '#js-flowbox-flow';
  var METAFIELD_SELECTOR = 'script[data-ugc-media]';
  var STRINGS_SELECTOR = 'script[data-ugc-strings]';
  var STRIP_SELECTOR = '[data-pdp-ugc-strip]';
  var SECTION_SELECTOR = '[data-section-type="main-product-rocket"]';
  var TARGET_WIDTH = 1200; // px requested from the Flowbox CDN
  var THUMB_WIDTH = 180; // px for the toggle button's own thumbnail (58px at 3x)
  var SCRAPE_TIMEOUT = 20000; // give up waiting for Flowbox after this
  var SCRAPE_SETTLE = 1000; // ms of no new posts before we consider it done

  /* ---------------- product metafield ---------------- */

  /**
   * The `custom.ugc` files, already normalised to the same item shape the
   * Flowbox scrape produces. Missing or malformed payload is not fatal: the
   * gallery simply falls back to Flowbox alone.
   */
  function readMetafieldMedia() {
    var el = document.querySelector(METAFIELD_SELECTOR);
    if (!el) return [];

    try {
      var parsed = JSON.parse(el.textContent);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(function (item) {
        return item && item.full;
      });
    } catch (e) {
      return [];
    }
  }

  /** Translated button labels, published by the section. */
  function readStrings() {
    var el = document.querySelector(STRINGS_SELECTOR);
    try {
      return el ? JSON.parse(el.textContent) : {};
    } catch (e) {
      return {};
    }
  }

  /* ---------------- Flowbox ---------------- */

  /** Re-encode a Flowbox CDN url at a different resize width. */
  function atWidth(url, width) {
    var m = String(url).match(/^(https:\/\/[^/]+\/image\/)([A-Za-z0-9_=-]+)$/);
    if (!m) return url;
    try {
      var payload = JSON.parse(
        atob(m[2].replace(/-/g, '+').replace(/_/g, '/'))
      );
      if (!payload.edits || !payload.edits.resize) return url;
      payload.edits.resize.width = width;
      delete payload.edits.resize.height;
      return (
        m[1] +
        btoa(JSON.stringify(payload)).replace(/\+/g, '-').replace(/\//g, '_')
      );
    } catch (e) {
      return url;
    }
  }

  function scrapeFlowbox() {
    var root = document.querySelector(FLOWBOX_SELECTOR);
    if (!root) return [];

    var seen = Object.create(null);
    var out = [];

    root.querySelectorAll('.bzfy-c-carousel-post').forEach(function (post) {
      var key = post.getAttribute('data-post-key');
      var img = post.querySelector('img.bzfy-o-image');
      if (!key || !img || seen[key]) return;

      var src = img.getAttribute('src');
      if (!src) return;

      seen[key] = true;
      out.push({
        key: key,
        full: atWidth(src, TARGET_WIDTH),
        thumb: atWidth(src, THUMB_WIDTH),
        alt: img.getAttribute('alt') || ''
      });
    });

    return out;
  }

  /**
   * The PDP strip block has already fetched the product's own Flowbox photos
   * (strip.ugcPosts, fallback posts left out); use the same ones so the button
   * and the strip agree.
   */
  function stripPhotos(strip) {
    return (strip.ugcPosts || []).map(function (post) {
      return {
        full: atWidth(post.src, TARGET_WIDTH),
        thumb: atWidth(post.src, THUMB_WIDTH),
        alt: post.alt
      };
    });
  }

  function waitForStrip(strip) {
    return new Promise(function (resolve) {
      if (strip.dataset.ugcState) return resolve(stripPhotos(strip));
      strip.addEventListener(
        'pdp-ugc-strip:done',
        function () {
          resolve(stripPhotos(strip));
        },
        { once: true }
      );
    });
  }

  /**
   * Older standalone Flowbox section: Flowbox streams posts in, so resolve once
   * the count has stopped growing for SCRAPE_SETTLE, or bail at SCRAPE_TIMEOUT.
   */
  function waitForFlowbox() {
    return new Promise(function (resolve) {
      var root = document.querySelector(FLOWBOX_SELECTOR);
      if (!root) return resolve([]);

      var settleTimer = null;
      var observer = null;
      var done = false;

      function finish() {
        if (done) return;
        done = true;
        if (settleTimer) clearTimeout(settleTimer);
        if (observer) observer.disconnect();
        clearTimeout(hardStop);
        resolve(scrapeFlowbox());
      }

      function poke() {
        if (settleTimer) clearTimeout(settleTimer);
        if (scrapeFlowbox().length) settleTimer = setTimeout(finish, SCRAPE_SETTLE);
      }

      var hardStop = setTimeout(finish, SCRAPE_TIMEOUT);

      observer = new MutationObserver(poke);
      observer.observe(root, { childList: true, subtree: true });
      poke(); // in case Flowbox already finished before we got here
    });
  }

  /* ---------------- markup ---------------- */

  function esc(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Deliberately flatter than a product slide. The theme's .rimage-* wrappers
   * size themselves from a padding-top ratio, which is what pushed portrait UGC
   * out of the desktop square and past the fold on mobile. A bare <img> that
   * fills the tile is easier to constrain — see the CSS.
   */
  function slidesMarkup(items) {
    return items
      .map(function (item, i) {
        var open =
          '<div class="slide ugc-slide' +
          (i === 0 ? ' slide--custom-initial' : '') +
          '" data-media-id="ugc-' +
          i +
          '">';

        // No .show-gallery wrapper on a video: that link opens the image
        // lightbox, which would swallow every click meant for the player.
        // `muted` is also set from JS in primeVideos() — parsed from an
        // innerHTML string the attribute alone does not always take, and
        // without it the autoplay is refused.
        if (item.type === 'video') {
          return (
            open +
            '<video class="ugc-slide__video" src="' +
            esc(item.full) +
            '"' +
            (item.poster ? ' poster="' + esc(item.poster) + '"' : '') +
            (item.alt ? ' aria-label="' + esc(item.alt) + '"' : '') +
            ' autoplay muted loop playsinline preload="metadata"></video>' +
            '</div>'
          );
        }

        return (
          open +
          '<a class="show-gallery" href="' +
          esc(item.full) +
          '">' +
          '<img class="ugc-slide__img" src="' +
          esc(item.full) +
          '" alt="' +
          esc(item.alt) +
          // The first shot is on screen the moment the view opens.
          (i === 0 ? '"' : '" loading="lazy"') +
          '>' +
          '</a></div>'
        );
      })
      .join('');
  }

  function thumbsMarkup(items) {
    return items
      .map(function (item, i) {
        return (
          '<a class="thumbnail thumbnail--media-image ugc-thumbnail' +
          (i === 0 ? ' selected' : '') +
          '" href="' +
          esc(item.full) +
          '" data-media-id="ugc-' +
          i +
          '">' +
          '<img class="ugc-thumbnail__img" src="' +
          esc(item.thumb) +
          '" alt="" loading="lazy">' +
          '</a>'
        );
      })
      .join('');
  }

  /* ---------------- toggle ---------------- */

  function UgcGalleryToggle(container, items) {
    this.container = container;
    this.items = items;
    this.gallery = container.querySelector('.gallery');
    this.mainImage = container.querySelector('.main-image');
    this.slideshow = container.querySelector('.product-slideshow');
    this.thumbnails = container.querySelector('.thumbnails');
    this.mode = 'media';
    // Known width/height ratios of the UGC media, by URL (see applyKnownRatios).
    this.ratios = Object.create(null);

    if (!this.gallery || !this.mainImage || !this.slideshow) return;

    // Keep the product markup verbatim so switching back is lossless. Taken
    // before the first swap, while the gallery is still in its initial state.
    this.mediaSlides = null;
    this.mediaThumbs = null;

    this.ugcSlides = slidesMarkup(items);
    this.ugcThumbs = thumbsMarkup(items);

    this.mountButton();
    this.bindVariantGuard();

    // Neither event bubbles, so listen in the capture phase.
    var onSized = this.onMediaSized.bind(this);
    this.slideshow.addEventListener('load', onSized, true);
    this.slideshow.addEventListener('loadedmetadata', onSized, true);

    // Bubble phase on purpose: Slick's own click handler on the track runs
    // first and swallows the click that ends a swipe.
    this.slideshow.addEventListener('click', this.onSlideClick.bind(this));
  }

  /**
   * The theme opens its gallery viewer from a document-level handler that
   * builds each thumbnail by cloning the slide's .rimage-outer-wrapper. UGC
   * slides are bare images without one, so the viewer's thumbnail row came
   * up empty. Open the same viewer here, with the UGC thumbnails, and keep
   * the click from reaching the theme.
   */
  UgcGalleryToggle.prototype.onSlideClick = function (e) {
    var link = e.target.closest('.ugc-slide .show-gallery');
    if (!link || this.mode !== 'ugc') return;
    if (!window.theme || typeof theme.buildGalleryViewer !== 'function') return;

    e.preventDefault();
    e.stopPropagation();

    var images = this.items
      .filter(function (item) {
        return item.type !== 'video';
      })
      .map(function (item) {
        var thumb = document.createElement('img');
        thumb.className = 'ugc-viewer-thumb';
        thumb.src = item.thumb;
        thumb.alt = '';
        return { thumbTag: thumb, zoomUrl: item.full };
      });

    theme.buildGalleryViewer({
      images: images,
      currentImage: link.getAttribute('href'),
      prev: theme.icons.chevronLeft,
      next: theme.icons.chevronRight,
      close: theme.icons.close
    });
  };

  UgcGalleryToggle.prototype.mountButton = function () {
    var productImg = this.slideshow.querySelector('.slide img');
    var productThumbSrc = productImg
      ? productImg.currentSrc || productImg.src
      : '';

    // The thumbnail is the whole button, so its name lives in aria-label.
    var strings = readStrings();

    var tabs = document.createElement('div');
    tabs.className = 'ugc-tabs';
    tabs.setAttribute('data-ugc-tabs', '');
    tabs.innerHTML =
      '<button type="button" class="ugc-tabs__btn" data-ugc-target="ugc" aria-pressed="false" aria-label="' +
      esc(strings.show_ugc || 'Customer photos') +
      '">' +
      '<span class="ugc-tabs__thumb"><img src="' +
      esc(this.items[0].thumb) +
      '" alt="" loading="lazy"></span>' +
      '</button>' +
      '<button type="button" class="ugc-tabs__btn is-hidden" data-ugc-target="media" aria-pressed="false" aria-label="' +
      esc(strings.show_media || 'Product photos') +
      '">' +
      '<span class="ugc-tabs__thumb"><img src="' +
      esc(productThumbSrc) +
      '" alt="" loading="lazy"></span>' +
      '</button>';

    var self = this;
    tabs.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-ugc-target]');
      if (!btn) return;
      e.preventDefault();
      self.switchTo(btn.getAttribute('data-ugc-target'));
    });

    this.tabs = tabs;
    this.mainImage.insertBefore(tabs, this.mainImage.firstChild);
    this.revealButton();
  };

  /**
   * The scrape only finishes seconds into the page's life, so the button would
   * otherwise pop into a gallery the shopper is already looking at. Fade it in
   * instead — and wait for its thumbnail first, so it does not fade in as a
   * blank square and then snap to the photo.
   */
  UgcGalleryToggle.prototype.revealButton = function () {
    var tabs = this.tabs;

    var show = function () {
      // Two frames: one for the inserted node to take its start styles, one to
      // flip them so the transition actually runs.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          tabs.classList.add('is-visible');
        });
      });
    };

    var img = tabs.querySelector('.ugc-tabs__btn:not(.is-hidden) img');
    if (!img || img.complete) return show();

    var fired = false;
    var once = function () {
      if (fired) return;
      fired = true;
      show();
    };

    img.addEventListener('load', once);
    img.addEventListener('error', once);
    setTimeout(once, 1500); // never leave the button invisible
  };

  UgcGalleryToggle.prototype.setButtonState = function () {
    var isUgc = this.mode === 'ugc';
    this.tabs.querySelectorAll('[data-ugc-target]').forEach(function (btn) {
      var target = btn.getAttribute('data-ugc-target');
      // Only the destination button is shown.
      var visible = isUgc ? target === 'media' : target === 'ugc';
      btn.classList.toggle('is-hidden', !visible);
      btn.setAttribute('aria-pressed', isUgc ? 'true' : 'false');
    });
  };

  UgcGalleryToggle.prototype.switchTo = function (mode) {
    if (mode === this.mode) return;
    if (!window.theme || typeof theme.initProductGallery !== 'function') return;

    // Measure before anything moves: product shots are short and UGC is tall,
    // so on mobile the swap would otherwise teleport the whole page.
    var fromHeight = this.shouldAnimate() ? this.mainImage.offsetHeight : 0;

    // First swap: snapshot the product markup so we can restore it exactly.
    // Read it now, before destroy, but strip Slick's wrappers by re-reading
    // after destroy instead — destroy unslicks, leaving the plain .slide list.
    theme.destroyProductGallery(this.container);

    if (this.mediaSlides === null) {
      this.mediaSlides = this.slideshow.innerHTML;
      this.mediaThumbs = this.thumbnails ? this.thumbnails.innerHTML : '';
    }

    if (mode === 'ugc') {
      this.slideshow.innerHTML = this.ugcSlides;
      if (this.thumbnails) this.thumbnails.innerHTML = this.ugcThumbs;
      // Before init, so Slick's clones copy the sizes too.
      this.applyKnownRatios();
    } else {
      this.slideshow.innerHTML = this.mediaSlides;
      if (this.thumbnails) this.thumbnails.innerHTML = this.mediaThumbs;
    }

    this.gallery.classList.remove('gallery-initialised');
    this.gallery.classList.toggle('gallery--ugc', mode === 'ugc');
    theme.initProductGallery(this.container);

    if (mode === 'ugc') {
      this.primeVideos();
      this.syncSlickHeight();
      // Again once the browser has laid the new slides out: a <video> often
      // has no box yet at the synchronous call above.
      requestAnimationFrame(this.syncSlickHeight.bind(this));
    }

    this.mode = mode;
    this.setButtonState();

    if (fromHeight) this.animateHeight(fromHeight);
  };

  /**
   * The slides are rebuilt from an HTML string on every swap, so each video is
   * a brand new element. Setting `muted` as a property (the attribute is not
   * reliably honoured on innerHTML-parsed media) and calling play() is what
   * actually starts it; a browser that still refuses just leaves the poster up.
   */
  UgcGalleryToggle.prototype.primeVideos = function () {
    this.slideshow
      .querySelectorAll('video.ugc-slide__video')
      .forEach(function (video) {
        video.muted = true;

        var start = function () {
          var played = video.play();
          if (played && played.catch) played.catch(function () {});
        };

        start();
        // A video that has not buffered yet rejects the first play(); try once
        // more when it is ready. A browser that still refuses keeps the poster.
        video.addEventListener('canplay', start, { once: true });
      });
  };

  /**
   * Mobile shows each UGC shot at its own height, like the product photos. A
   * Flowbox URL carries no dimensions, so a slide starts as a 4:5 placeholder
   * (see the CSS) and takes the real ratio once its image or video knows it.
   */
  UgcGalleryToggle.prototype.onMediaSized = function (e) {
    var media = e.target;
    if (!media.matches || !media.matches('.ugc-slide__img, .ugc-slide__video')) return;

    var w = media.naturalWidth || media.videoWidth;
    var h = media.naturalHeight || media.videoHeight;
    if (!w || !h) return;

    var ratio = w + ' / ' + h;
    this.ratios[media.getAttribute('src')] = ratio;

    // Already sized from the thumbnail (same shot, different resolution): a
    // re-apply here would cut the toggle's own tween short for nothing.
    var current = String(media.style.aspectRatio).split('/');
    if (current.length === 2 && Math.abs(current[0] / current[1] - w / h) < 0.01 * (w / h)) return;

    // The active slide may grow or shrink now: tween it like the toggle does
    // rather than snapping (the theme's jQuery height animation only runs on
    // slide changes).
    var fromHeight = this.shouldAnimate() ? this.mainImage.offsetHeight : 0;
    media.style.aspectRatio = ratio;
    this.syncSlickHeight();
    if (fromHeight) this.animateHeight(fromHeight);
  };

  /**
   * Give the UGC slides their real size up front, so opening the view is one
   * smooth resize instead of a tween to the 4:5 placeholder followed by a jump
   * once the photo arrives. The first shot's ratio comes free from the toggle
   * button's thumbnail (same image, already loaded); any shot seen before is
   * remembered from onMediaSized.
   */
  UgcGalleryToggle.prototype.applyKnownRatios = function () {
    var self = this;
    var thumb = this.tabs && this.tabs.querySelector('[data-ugc-target="ugc"] img');
    var first = this.items[0];
    if (thumb && thumb.naturalWidth && first && !this.ratios[first.full]) {
      this.ratios[first.full] = thumb.naturalWidth + ' / ' + thumb.naturalHeight;
    }

    this.slideshow
      .querySelectorAll('.ugc-slide__img, .ugc-slide__video')
      .forEach(function (media) {
        var ratio = self.ratios[media.getAttribute('src')];
        if (ratio) media.style.aspectRatio = ratio;
      });
  };

  /**
   * Mobile only (desktop is a CSS collage with no Slick at all).
   *
   * Slick's adaptiveHeight sizes .slick-list from the active slide when it
   * inits or changes slide, and that list is overflow:hidden. A UGC slide
   * resizes after that (its media arrives with the real ratio, or a <video>
   * has no box yet), so match the list to the active slide again — growing
   * or shrinking — whenever that happens.
   */
  UgcGalleryToggle.prototype.syncSlickHeight = function () {
    var list = this.slideshow.querySelector('.slick-list');
    if (!list) return;

    var slide =
      this.slideshow.querySelector('.slick-slide.slick-active') ||
      this.slideshow.querySelector('.slide');
    if (!slide) return;

    var wanted = slide.offsetHeight;
    if (wanted && Math.abs(wanted - list.offsetHeight) > 1) list.style.height = wanted + 'px';
  };

  /** Mobile is where the jump hurts; desktop swaps a 2800px column either way. */
  UgcGalleryToggle.prototype.shouldAnimate = function () {
    return (
      window.matchMedia('(max-width: 767px)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  };

  /**
   * Tween .main-image from its old height to the height the rebuilt gallery
   * just took. `overflow: hidden` for the duration means the new photo is
   * revealed (or collapsed) behind the moving edge instead of jumping, and
   * everything below the gallery slides with it.
   */
  UgcGalleryToggle.prototype.animateHeight = function (fromHeight) {
    var box = this.mainImage;
    var self = this;

    if (this._heightCleanup) this._heightCleanup();

    // Wait a frame so Slick has applied its adaptiveHeight before we measure.
    requestAnimationFrame(function () {
      box.style.height = '';
      var toHeight = box.offsetHeight;
      if (!toHeight || Math.abs(toHeight - fromHeight) < 2) return;

      box.classList.add('is-resizing');
      box.style.height = fromHeight + 'px';
      void box.offsetHeight; // commit the start height before transitioning
      box.style.height = toHeight + 'px';

      var finish = function (e) {
        if (e && e.propertyName && e.propertyName !== 'height') return;
        box.removeEventListener('transitionend', finish);
        clearTimeout(timer);
        box.classList.remove('is-resizing');
        box.style.height = '';
        self._heightCleanup = null;
      };

      // transitionend can be missed if the element is re-rendered mid-flight.
      var timer = setTimeout(finish, 700);
      box.addEventListener('transitionend', finish);
      self._heightCleanup = finish;
    });
  };

  /**
   * Size / variant options repaint the main image by looking up a slide by its
   * media id. Those ids do not exist while UGC is showing, so step back to the
   * product media first and let the theme's own handler run against it.
   * (Colour is a separate product URL here, so it reloads the page anyway.)
   */
  UgcGalleryToggle.prototype.bindVariantGuard = function () {
    var self = this;
    var revert = function () {
      if (self.mode === 'ugc') self.switchTo('media');
    };
    ['change', 'click'].forEach(function (type) {
      self.container.addEventListener(
        type,
        function (e) {
          if (
            e.target.closest(
              '[data-option-index], .original-selector, .swatch, .variant-input'
            )
          ) {
            revert();
          }
        },
        true // capture: run before the theme's own handler
      );
    });
  };

  /* ---------------- boot ---------------- */

  function init() {
    var container = document.querySelector(SECTION_SELECTOR);
    if (!container || container.dataset.ugcToggleBound) return;

    // Section setting: 'both', 'mobile' or 'desktop' (the theme's 768px
    // breakpoint). Decided once at load; ugc-gallery-toggle.css hides the button
    // if the window is later resized across the breakpoint.
    var devices = readStrings().devices || 'both';
    var isMobile = window.matchMedia('(max-width: 767px)').matches;
    if ((devices === 'mobile' && !isMobile) || (devices === 'desktop' && isMobile)) return;
    container.dataset.ugcDevices = devices;

    // No Flowbox on the page, no button.
    var strip = document.querySelector(STRIP_SELECTOR);
    if (!strip && !document.querySelector(FLOWBOX_SELECTOR)) return;

    container.dataset.ugcToggleBound = 'true';

    (strip ? waitForStrip(strip) : waitForFlowbox())
      .then(function (scraped) {
        if (!scraped || !scraped.length) return;
        // The product's own media leads; Flowbox is the tail of the same reel.
        new UgcGalleryToggle(container, readMetafieldMedia().concat(scraped));
      })
      .catch(function () {
        /* Flowbox missing or blocked: no photos, no button. */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
